import { useEffect, useState, Component, lazy, Suspense } from 'react';
import axios from 'axios';
import { fetchPageConfig } from '../../hooks/useSiteData';

const ZapChapeauWidget = lazy(() => import('./ZapChapeauWidget'));

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CONFIG = {
  enabled: true,
  title: 'Zap Chapeau',
  transition: 'fade',
  autoplay: true,
  speed_ms: 5000,
  allow_manual: true,
  categories: [],
  max_articles: 10,
  order: 'recent',
  show_category: true,
  show_date: true,
  fallback_image: '',
};

class ZapErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) { console.error('[ZapChapeau]', err); }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export default function ZapChapeauLauncher({ articles, categories }) {
  const [config, setConfig] = useState(null);
  // Articles référencés par la sélection manuelle mais absents de la liste de la
  // home (qui ne contient que les plus récents). Résolus à la demande, cf. effet.
  const [extraArticles, setExtraArticles] = useState([]);

  useEffect(() => {
    let alive = true;
    fetchPageConfig()
      .then(data => {
        if (!alive) return;
        const merged = { ...DEFAULT_CONFIG, ...(data?.zap_chapeau || {}) };
        setConfig(merged);
      })
      .catch(() => { if (alive) setConfig(DEFAULT_CONFIG); });
    return () => { alive = false; };
  }, []);

  // Sélection manuelle : garantir que TOUS les articles sélectionnés par l'admin
  // sont disponibles, même s'ils ne figurent pas dans les articles récents de la
  // home. Sinon seuls ceux déjà présents s'affichent (bug : 15 sélectionnés ->
  // 4 affichés). Même logique que la page publique /zap.
  useEffect(() => {
    if (!config) return;
    let alive = true;
    const knownIds = new Set((articles || []).map((a) => a?.id).filter(Boolean));
    const manualArticleIds = [...new Set(
      (config.manual_items || [])
        .filter((it) => it && it.type === 'article' && it.article_id && !knownIds.has(it.article_id))
        .map((it) => it.article_id)
    )];
    if (manualArticleIds.length === 0) { setExtraArticles([]); return () => { alive = false; }; }
    Promise.allSettled(
      manualArticleIds.map((id) =>
        axios.get(`${API_URL}/api/articles/${encodeURIComponent(id)}`).then((r) => r.data)
      )
    ).then((results) => {
      if (!alive) return;
      setExtraArticles(
        results
          .filter((r) => r.status === 'fulfilled' && r.value && r.value.id)
          .map((r) => r.value)
      );
    });
    return () => { alive = false; };
  }, [config, articles]);

  if (!config || !config.enabled) return null;

  // Si des items manuels existent, on autorise le rendu même sans articles dans
  // la liste (ex : que des vidéos YouTube). Sinon on garde le garde-fou d'origine.
  const hasManual = Array.isArray(config.manual_items) && config.manual_items.length > 0;
  if (!hasManual && (!articles || articles.length === 0)) return null;

  // Le widget déduplique par article_id : un éventuel recouvrement est sans effet.
  const mergedArticles = extraArticles.length > 0
    ? [...(articles || []), ...extraArticles]
    : (articles || []);

  return (
    <ZapErrorBoundary>
      <Suspense fallback={null}>
        <ZapChapeauWidget articles={mergedArticles} categories={categories} config={config} />
      </Suspense>
    </ZapErrorBoundary>
  );
}
